import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Divider, Form, Input, InputNumber, Modal, Radio, Row, Select, Switch, Table, Tag, message } from 'antd';
import SaleCheck from './SaleCheck';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeftOutlined, SendOutlined, UserOutlined, EnvironmentOutlined, ShoppingOutlined, PrinterOutlined, CalendarOutlined, FileTextOutlined, SettingOutlined, ShopOutlined, InboxOutlined, AppstoreOutlined } from '@ant-design/icons';
import css from './OrderSingle.module.css';

// BTS modal bo'lim sarlavhasi — ikonka + rang
const secTitle = (icon, text, color) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, color, fontWeight: 600, fontSize: 14 }}>
        {icon}<span>{text}</span>
    </span>
);

// Buyurtma holati (order_status) uchun o'qiladigan yorliq va rang
const ORDER_STATUS = {
    1: { label: 'Yangi buyurtma', color: 'blue' },
    2: { label: 'Qabul qilindi', color: 'cyan' },
    3: { label: "Yig'ilmoqda", color: 'gold' },
    4: { label: 'Yetkazilmoqda', color: 'orange' },
    5: { label: 'Yetkazildi', color: 'green' },
    6: { label: 'Bekor qilindi', color: 'red' },
};
const statusMeta = (s) => ORDER_STATUS[s] || { label: '—', color: 'default' };
const fmtSum = (v) => Number(v || 0).toLocaleString('ru-RU').replace(/,/g, ' ');

/**
 * Berilgan obyektdan kalitlar ro'yxati bo'yicha birinchi mavjud (null/undefined emas) qiymatni qaytaradi.
 */
const firstDefined = (obj, keys) => {
    if (!obj) return undefined;
    for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
    }
    return undefined;
};

/**
 * BTS katalog massivini Ant Select options ga aylantiradi (himoyalangan: turli javob shakllariga moslashadi).
 */
const toOptions = (arr, codeKeys, nameKeys) =>
    (Array.isArray(arr) ? arr : [])
        .map((it) => {
            const code = firstDefined(it, codeKeys);
            const name = firstDefined(it, nameKeys);
            if (code === undefined) return null;
            return {
                value: code,
                label: name !== undefined ? `${name} (${code})` : String(code),
            };
        })
        .filter(Boolean);

/**
 * BTS katalog javobidan ro'yxatni ajratib oladi.
 * Backend {data:[...]} yoki {data:{items:[...]}} — ikkalasiga ham bardoshli.
 */
const unwrapList = (resp) => {
    const d = resp?.data?.data ?? resp?.data ?? [];
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.items)) return d.items;
    return [];
};

/**
 * Telefon raqamini +998XXXXXXXXX formatiga keltiradi.
 */
const normalizePhone = (raw) => {
    if (!raw) return undefined;
    let digits = String(raw).replace(/\D/g, '');
    if (digits.startsWith('998')) digits = digits.slice(3);
    if (digits.length === 9) return `+998${digits}`;
    return raw; // formatga tushmasa, foydalanuvchi qo'lda tuzatadi
};

const PHONE_RULE = { pattern: /^\+998\d{9}$/, message: '+998XXXXXXXXX formatida bo`lishi kerak' };

/** Nomni taqqoslash uchun normallashtiradi (registr, bo'sh joy, belgilarni olib tashlaydi). */
const normName = (s) => String(s || '').toLowerCase().replace(/[’'`]/g, '').replace(/[^a-zа-яё0-9]/gi, '');

/** Massivdan nomi bo'yicha mos elementni topadi (to'liq yoki qisman moslik). */
const matchByName = (list, name) => {
    const target = normName(name);
    if (!target) return undefined;
    return (Array.isArray(list) ? list : []).find((it) => {
        const n = normName(firstDefined(it, ['name', 'nameuz', 'title']));
        return n && (n === target || n.includes(target) || target.includes(n));
    });
};

function OrderSingle() {
    const [order, setOrder] = useState({});
    const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
    const [deliverySubmitting, setDeliverySubmitting] = useState(false);
    const [btsstartdata, setBtsstartdata] = useState({});
    const [deliveryForm] = Form.useForm();

    // BTS katalog ma'lumotlari
    const [regions, setRegions] = useState([]);
    const [senderCities, setSenderCities] = useState([]);
    const [receiverCities, setReceiverCities] = useState([]);
    const [receiverBranches, setReceiverBranches] = useState([]);
    const [packages, setPackages] = useState([]);
    const [postTypes, setPostTypes] = useState([]);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [vrWeights, setVrWeights] = useState([]);

    const { id } = useParams();
    const navigate = useNavigate();

    // ---- Buyurtma + boshlang'ich ma'lumotlar ----
    useEffect(() => {
        axios.get(`/order/${id}`).then((res) => setOrder(res.data)).catch(() => {});
        axios.get('/btsstartdata').then((res) => setBtsstartdata(res.data)).catch(() => {});
        // Variant og'irlik/gabarit ma'lumotlari (cargo ni avtomatik to'ldirish uchun)
        axios.get('/vr_weights').then((res) => setVrWeights(Array.isArray(res.data) ? res.data : [])).catch(() => {});
    }, [id]);

    const itemsPieceCount = useMemo(() => {
        if (!Array.isArray(order?.items)) return 1;
        const sum = order.items.reduce((a, b) => a + (Number(b?.count) || 0), 0);
        return sum > 0 ? sum : 1;
    }, [order]);

    // Buyurtma mahsulotlarini vr_weights bilan bog'lab cargo qiymatlarini hisoblaymiz
    const cargoDefaults = useMemo(() => {
        const items = Array.isArray(order?.items) ? order.items : [];
        if (!items.length || !vrWeights.length) {
            return { weight: 1, volume: 0, packageId: undefined, postTypeId: undefined };
        }
        let weight = 0;
        let volume = 0;
        let packageId;
        let postTypeId;
        items.forEach((it) => {
            const vrId = it?.pr_vr_id ?? it?.id;
            const row = vrWeights.find((w) => Number(w.id) === Number(vrId) || Number(w.vr_id) === Number(vrId));
            const count = Number(it?.count) || 1;
            if (row) {
                weight += (Number(row.weight) || 0) * count;
                volume += (Number(row.volume) || 0) * count;
                if (packageId === undefined && row.packageId != null) packageId = Number(row.packageId);
                if (postTypeId === undefined && row.postTypeId != null) postTypeId = Number(row.postTypeId);
            }
        });
        return {
            weight: weight > 0 ? Number(weight.toFixed(3)) : 1,
            volume: volume > 0 ? Number(volume.toFixed(3)) : 0,
            packageId,
            postTypeId,
        };
    }, [order, vrWeights]);

    const initialDeliveryValues = useMemo(() => ({
        clientId: order?.id ? String(order.id) : undefined,
        wcs_code: order?.id ? `ORDER-${order.id}` : undefined,
        pickup_type: 'courier',
        dropoff_type: 'courier',
        is_sender_location: false,
        is_receiver_location: false,
        ready_to_take: true,
        bringBackMoney: 0,
        takePhoto: 0,
        bringBackWaybill: 0,
        piece: itemsPieceCount,
        volume: cargoDefaults.volume,
        weight: cargoDefaults.weight,
        packageId: cargoDefaults.packageId,
        postTypeId: cargoDefaults.postTypeId,
        back_money: order?.total_summ != null ? Number(order.total_summ) : undefined,
        receiver_name: order?.username || undefined,
        receiver_phone: normalizePhone(order?.phone),
        receiver_address: order?.address || undefined,
    }), [order, itemsPieceCount, cargoDefaults]);

    // ---- Katalogni modal ochilganda yuklash ----
    const loadCatalog = async () => {
        setCatalogLoading(true);
        try {
            const [reg, pkg, pt] = await Promise.all([
                axios.get('/bts/regions'),
                axios.get('/bts/packages'),
                axios.get('/bts/post-types'),
            ]);
            const regList = unwrapList(reg);
            setRegions(regList);
            setPackages(unwrapList(pkg));
            setPostTypes(unwrapList(pt));
            return regList;
        } catch (e) {
            message.warning('BTS katalogini yuklab bo`lmadi. Sozlamalarni tekshiring.');
            return [];
        } finally {
            setCatalogLoading(false);
        }
    };

    const loadCities = async (regionCode, target) => {
        try {
            const res = await axios.get('/bts/cities', { params: { region_code: regionCode, regionCode } });
            const list = unwrapList(res);
            if (target === 'sender') setSenderCities(list);
            else setReceiverCities(list);
            return list;
        } catch (e) {
            message.warning('Shaharlarni yuklab bo`lmadi');
            return [];
        }
    };

    const loadBranches = async (cityCode) => {
        try {
            const res = await axios.get('/bts/branches', { params: { city_code: cityCode, cityCode } });
            setReceiverBranches(unwrapList(res));
        } catch (e) {
            message.warning('Filiallarni yuklab bo`lmadi');
        }
    };

    // Buyurtmadagi viloyat/tuman nomidan BTS region/shaharni avtomatik topib qo'yamiz
    const autofillReceiverLocation = async (regionList) => {
        if (!order?.region) return;
        const reg = matchByName(regionList, order.region);
        if (!reg) return;
        const regCode = firstDefined(reg, ['code', 'region_code', 'id']);
        deliveryForm.setFieldsValue({ receiver_region: regCode });
        const cities = await loadCities(regCode, 'receiver');
        const city = matchByName(cities, order.district);
        if (city) {
            deliveryForm.setFieldsValue({ receiver_city_code: firstDefined(city, ['code', 'city_code', 'id']) });
        }
    };

    const openDeliveryModal = async () => {
        deliveryForm.setFieldsValue(initialDeliveryValues);
        setDeliveryModalOpen(true);
        // Yagona do'kon bo'lsa jo'natuvchini avtomatik to'ldiramiz
        const shops = btsstartdata?.shops || [];
        if (shops.length) onSenderShopChange(shops[0].id);
        const regList = await loadCatalog();
        autofillReceiverLocation(regList);
    };

    const closeDeliveryModal = () => {
        setDeliveryModalOpen(false);
        deliveryForm.resetFields();
    };

    // Jo'natuvchi do'konni tanlaganda sender maydonlarini to'ldirish
    const onSenderShopChange = (shopId) => {
        const shop = (btsstartdata?.shops || []).find((s) => s.id === shopId);
        if (!shop) return;
        deliveryForm.setFieldsValue({
            sender_name: shop.shopname,
            sender_phone: normalizePhone(shop.phone),
            sender_address: shop.address,
        });
    };

    // ---- BTS ga yuborish ----
    const onSubmitDelivery = async (values) => {
        try {
            setDeliverySubmitting(true);

            const isSenderLoc = !!values.is_sender_location;
            const isReceiverLoc = !!values.is_receiver_location;

            const sender = {
                name: values.sender_name,
                phone: values.sender_phone,
                address: values.sender_address,
                ...(isSenderLoc
                    ? { latitude: values.sender_latitude, longitude: values.sender_longitude }
                    : { city_code: String(values.sender_city_code) }),
                ...(values.sender_branch_code ? { branch_code: String(values.sender_branch_code) } : {}),
            };

            const receiver = {
                name: values.receiver_name,
                phone: values.receiver_phone,
                ...(values.receiver_phone1 ? { phone1: values.receiver_phone1 } : {}),
                address: values.receiver_address,
                ...(isReceiverLoc
                    ? { latitude: values.receiver_latitude, longitude: values.receiver_longitude }
                    : { city_code: String(values.receiver_city_code) }),
                ...(values.dropoff_type === 'branch' && values.receiver_branch_code
                    ? { branch_code: String(values.receiver_branch_code) }
                    : {}),
            };

            // postTypes — buyurtma mahsulotlaridan avtomatik tuziladi
            const postTypesArr = (Array.isArray(order?.items) ? order.items : []).map((it) => ({
                name: it?.name || 'Mahsulot',
                code: String(it?.pr_vr_id ?? it?.id ?? ''),
                count: Number(it?.count) || 1,
                cost: Number(it?.price) || 0,
            }));

            const cargo = {
                weight: Number(values.weight),
                volume: values.volume != null ? Number(values.volume) : 0,
                piece: Number(values.piece) || 1,
                ...(values.packageId ? { packageId: Number(values.packageId) } : {}),
                ...(values.postTypeId ? { postTypeId: Number(values.postTypeId) } : {}),
                ...(postTypesArr.length ? { postTypes: postTypesArr } : {}),
            };

            const payload = {
                clientId: values.clientId,
                wcs_code: values.wcs_code,
                pickup_type: values.pickup_type,
                dropoff_type: values.dropoff_type,
                is_sender_location: isSenderLoc,
                is_receiver_location: isReceiverLoc,
                ready_to_take: !!values.ready_to_take,
                sender,
                receiver,
                cargo,
                bringBackMoney: Number(values.bringBackMoney) ? 1 : 0,
                takePhoto: Number(values.takePhoto) ? 1 : 0,
                bringBackWaybill: Number(values.bringBackWaybill) ? 1 : 0,
                ...(Number(values.bringBackMoney) ? { back_money: Number(values.back_money) || 0 } : {}),
            };

            const res = await axios.post('/bts/orders', payload);

            if (res?.data?.success) {
                const btsId = res.data?.data?.orderId ?? res.data?.data?.id ?? '';
                message.success(`Yetkazish buyurtmasi yuborildi${btsId ? ` (BTS #${btsId})` : ''}`);
                closeDeliveryModal();
            } else {
                message.error(res?.data?.message || 'BTS javobi muvaffaqiyatsiz');
            }
        } catch (e) {
            const data = e?.response?.data;
            const errs = data?.errors;
            if (errs && typeof errs === 'object') {
                Object.values(errs).flat().forEach((m) => message.error(String(m)));
            } else {
                message.error(data?.message || 'Yetkazish buyurtmasini yuborishda xatolik');
            }
        } finally {
            setDeliverySubmitting(false);
        }
    };

    const regionOptions = toOptions(regions, ['code', 'region_code', 'id'], ['name', 'nameuz', 'title']);
    const senderCityOptions = toOptions(senderCities, ['code', 'city_code', 'id'], ['name', 'nameuz', 'title']);
    const receiverCityOptions = toOptions(receiverCities, ['code', 'city_code', 'id'], ['name', 'nameuz', 'title']);
    const branchOptions = toOptions(receiverBranches, ['code', 'branch_code', 'id'], ['name', 'address', 'title']);
    const packageOptions = toOptions(packages, ['id', 'packageId', 'code'], ['name', 'nameuz', 'title']);
    const postTypeOptions = toOptions(postTypes, ['id', 'postTypeId', 'code'], ['name', 'nameuz', 'title']);

    const itemsTotal = Number(order.items?.reduce((a, b) => a + Number(b.summa), 0) || 0);
    const grandTotal = itemsTotal + Number(order.deliversumm || 0) - Number(order.discount || 0);
    const st = statusMeta(order.order_status);

    return (
        <div className={css.wrap}>
            {/* ---- Sarlavha ---- */}
            <div className={css.topbar}>
                <div className={css.titleRow}>
                    <Button onClick={() => navigate(-1)} icon={<ArrowLeftOutlined />}>Orqaga</Button>
                    <h2 className={css.orderTitle}>Buyurtma #{order.id}</h2>
                    <Tag color={st.color} style={{ fontSize: 13, padding: '3px 12px', borderRadius: 20, margin: 0 }}>{st.label}</Tag>
                </div>
                <span className={css.orderDate}><CalendarOutlined /> {order.created_at}</span>
            </div>

            <div className={css.layout}>
                <div className={css.mainCol}>
                    {/* ---- Mijoz + Manzil ---- */}
                    <div className={css.infoGrid}>
                        <div className={css.card}>
                            <h4 className={css.cardTitle}><UserOutlined /> Mijoz</h4>
                            <div className={css.row}><span className={css.rlabel}>Ismi</span><span className={css.rvalue}>{order.username}</span></div>
                            <div className={css.row}><span className={css.rlabel}>Telefon</span><span className={css.rvalue}>{order.phone || '—'}</span></div>
                            <div className={css.row}><span className={css.rlabel}>To'lov turi</span><span className={css.rvalue}>{order.paytype || '—'}</span></div>
                        </div>
                        <div className={css.card}>
                            <h4 className={css.cardTitle}><EnvironmentOutlined /> Yetkazish manzili</h4>
                            <div className={css.row}><span className={css.rlabel}>Viloyat</span><span className={css.rvalue}>{order.region}</span></div>
                            <div className={css.row}><span className={css.rlabel}>Tuman</span><span className={css.rvalue}>{order.district}</span></div>
                            <div className={css.row}><span className={css.rlabel}>Manzil</span><span className={css.rvalue}>{order.address}</span></div>
                            <div className={css.row}><span className={css.rlabel}>Pochta indeksi</span><span className={css.rvalue}>{order.post_index || '—'}</span></div>
                        </div>
                    </div>

                    {/* ---- Mahsulotlar ---- */}
                    <div className={css.card} style={{ paddingBottom: 18 }}>
                        <h4 className={css.cardTitle}><ShoppingOutlined /> Mahsulotlar</h4>
                        <Table
                            columns={[
                                { title: 'Nomi', dataIndex: 'name', key: 'name' },
                                { title: 'Soni', dataIndex: 'count', key: 'count', align: 'center', width: 90 },
                                { title: 'Narxi', dataIndex: 'price', key: 'price', align: 'right', width: 140, render: (v) => fmtSum(v) },
                                { title: 'Jami', dataIndex: 'summa', key: 'summa', align: 'right', width: 140, render: (v) => <b>{fmtSum(v)}</b> },
                            ]}
                            dataSource={order.items}
                            size="small"
                            pagination={false}
                            scroll={{ y: 240 }}
                            rowKey="id"
                        />
                    </div>

                    {/* ---- Summalar ---- */}
                    <div className={css.infoGrid}>
                        <div className={css.card}>
                            <h4 className={css.cardTitle}><FileTextOutlined /> Buyurtma ma'lumotlari</h4>
                            <div className={css.row}><span className={css.rlabel}>Raqami</span><span className={css.rvalue}>#{order.id}</span></div>
                            <div className={css.row}><span className={css.rlabel}>Holati</span><span className={css.rvalue}><Tag color={st.color} style={{ margin: 0 }}>{st.label}</Tag></span></div>
                            <div className={css.row}><span className={css.rlabel}>Sana</span><span className={css.rvalue}>{order.created_at}</span></div>
                        </div>
                        <div className={css.card}>
                            <h4 className={css.cardTitle}>Summalar</h4>
                            <div className={css.row}><span className={css.rlabel}>Mahsulotlar</span><span className={css.rvalue}>{fmtSum(itemsTotal)} so'm</span></div>
                            <div className={css.row}><span className={css.rlabel}>Chegirma</span><span className={css.rvalue} style={{ color: 'var(--danger, #cf1322)' }}>− {fmtSum(order.discount)} so'm</span></div>
                            <div className={css.row}><span className={css.rlabel}>Yetkazib berish</span><span className={css.rvalue}>{fmtSum(order.deliversumm)} so'm</span></div>
                            <div className={css.totalRow}><span className={css.totalLabel}>Jami</span><span className={css.totalVal}>{fmtSum(grandTotal)} so'm</span></div>
                        </div>
                    </div>

                    {/* ---- Amallar ---- */}
                    <div className={css.actions}>
                        <Button size="large" icon={<PrinterOutlined />} style={{ background: '#0d9488', borderColor: '#0d9488', color: '#fff' }}>Fakturani chop etish</Button>
                        <Button size="large" icon={<PrinterOutlined />} type="primary">Xarid chekini chop etish</Button>
                        <Button size="large" icon={<SendOutlined />} style={{ background: '#6d28d9', borderColor: '#6d28d9', color: '#fff' }} onClick={openDeliveryModal}>BTS ga yetkazish</Button>
                    </div>
                </div>

                {/* ---- Chek ---- */}
                <div className={css.sideCol}>
                    <SaleCheck order={order} />
                </div>
            </div>

            <Modal
                title={<span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 17 }}><span style={{ width: 34, height: 34, borderRadius: 9, background: '#6d28d9', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><SendOutlined /></span> BTS yetkazish buyurtmasi</span>}
                open={deliveryModalOpen}
                onCancel={closeDeliveryModal}
                footer={null}
                destroyOnClose
                width={880}
                styles={{ body: { paddingTop: 12, background: 'var(--bg-main, #f7f8fa)', maxHeight: '75vh', overflowY: 'auto' } }}
            >
                <Form
                    form={deliveryForm}
                    layout="vertical"
                    onFinish={onSubmitDelivery}
                    initialValues={initialDeliveryValues}
                >
                    {/* Asosiy sozlamalar */}
                    <Card size="small" title={secTitle(<SettingOutlined />, 'Asosiy sozlamalar', '#6366f1')} style={{ marginBottom: 14, borderRadius: 12 }} styles={{ header: { background: '#6366f10f', borderBottom: '1px solid #6366f122' } }}>
                        <Row gutter={16}>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="clientId" label="Mijoz kodi">
                                    <Input placeholder="ORDER ID" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="wcs_code" label="WCS kod">
                                    <Input placeholder="Unique code" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="pickup_type" label="Olib ketish" rules={[{ required: true }]}>
                                    <Radio.Group optionType="button" buttonStyle="solid">
                                        <Radio.Button value="courier">Kuryer</Radio.Button>
                                        <Radio.Button value="self">O'zi</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="dropoff_type" label="Yetkazish" rules={[{ required: true }]}>
                                    <Radio.Group optionType="button" buttonStyle="solid">
                                        <Radio.Button value="courier">Kuryer</Radio.Button>
                                        <Radio.Button value="branch">Filial</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Row gutter={16}>
                        {/* Jo'natuvchi */}
                        <Col xs={24} md={12}>
                            <Card size="small" title={secTitle(<ShopOutlined />, "Jo'natuvchi", '#2563eb')} style={{ borderRadius: 12, height: '100%' }} styles={{ header: { background: '#2563eb0f', borderBottom: '1px solid #2563eb22' } }}>
                                <Row gutter={12}>
                                    <Col xs={24}>
                                        <Form.Item label="Do'kondan to'ldirish">
                                            <Select
                                                allowClear
                                                placeholder="Do'konni tanlang"
                                                onChange={onSenderShopChange}
                                                options={(btsstartdata?.shops || []).map((s) => ({ value: s.id, label: s.shopname }))}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="sender_name" label="F.I.O" rules={[{ required: true, message: 'F.I.O kiriting' }]}>
                                            <Input placeholder="Ism Familiya" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="sender_phone" label="Telefon" rules={[{ required: true }, PHONE_RULE]}>
                                            <Input placeholder="+998..." />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item name="sender_address" label="Manzil" rules={[{ required: true, message: 'Manzilni kiriting' }]}>
                                            <Input placeholder="Manzil" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item name="is_sender_location" label="Koordinata bo'yicha" valuePropName="checked">
                                            <Switch checkedChildren="Lat/Lng" unCheckedChildren="Shahar kodi" />
                                        </Form.Item>
                                    </Col>

                                    <Form.Item shouldUpdate={(p, c) => p.is_sender_location !== c.is_sender_location} noStyle>
                                        {({ getFieldValue }) => getFieldValue('is_sender_location') ? (
                                            <>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item name="sender_latitude" label="Latitude" rules={[{ required: true }]}>
                                                        <InputNumber style={{ width: '100%' }} step={0.000001} />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item name="sender_longitude" label="Longitude" rules={[{ required: true }]}>
                                                        <InputNumber style={{ width: '100%' }} step={0.000001} />
                                                    </Form.Item>
                                                </Col>
                                            </>
                                        ) : (
                                            <>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item name="sender_region" label="Viloyat">
                                                        <Select
                                                            showSearch optionFilterProp="label" loading={catalogLoading}
                                                            placeholder="Viloyat" options={regionOptions}
                                                            onChange={(v) => { loadCities(v, 'sender'); deliveryForm.setFieldsValue({ sender_city_code: undefined }); }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item name="sender_city_code" label="Shahar kodi" rules={[{ required: true, message: 'Shaharni tanlang' }]}>
                                                        <Select showSearch optionFilterProp="label" placeholder="Shahar" options={senderCityOptions} />
                                                    </Form.Item>
                                                </Col>
                                            </>
                                        )}
                                    </Form.Item>
                                </Row>
                            </Card>
                        </Col>

                        {/* Qabul qiluvchi */}
                        <Col xs={24} md={12}>
                            <Card size="small" title={secTitle(<EnvironmentOutlined />, 'Qabul qiluvchi', '#16a34a')} style={{ borderRadius: 12, height: '100%' }} styles={{ header: { background: '#16a34a0f', borderBottom: '1px solid #16a34a22' } }}>
                                <Row gutter={12}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="receiver_name" label="F.I.O" rules={[{ required: true, message: 'F.I.O kiriting' }]}>
                                            <Input placeholder="Ism Familiya" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="receiver_phone" label="Telefon" rules={[{ required: true }, PHONE_RULE]}>
                                            <Input placeholder="+998..." />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="receiver_phone1" label="Qo'shimcha telefon" rules={[PHONE_RULE]}>
                                            <Input placeholder="+998..." />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item name="receiver_address" label="Manzil" rules={[{ required: true, message: 'Manzilni kiriting' }]}>
                                            <Input placeholder="Manzil" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item name="is_receiver_location" label="Koordinata bo'yicha" valuePropName="checked">
                                            <Switch checkedChildren="Lat/Lng" unCheckedChildren="Shahar kodi" />
                                        </Form.Item>
                                    </Col>

                                    <Form.Item shouldUpdate={(p, c) => p.is_receiver_location !== c.is_receiver_location || p.dropoff_type !== c.dropoff_type} noStyle>
                                        {({ getFieldValue }) => {
                                            const loc = getFieldValue('is_receiver_location');
                                            const isBranch = getFieldValue('dropoff_type') === 'branch';
                                            if (loc) {
                                                return (
                                                    <>
                                                        <Col xs={24} sm={12}>
                                                            <Form.Item name="receiver_latitude" label="Latitude" rules={[{ required: true }]}>
                                                                <InputNumber style={{ width: '100%' }} step={0.000001} />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={24} sm={12}>
                                                            <Form.Item name="receiver_longitude" label="Longitude" rules={[{ required: true }]}>
                                                                <InputNumber style={{ width: '100%' }} step={0.000001} />
                                                            </Form.Item>
                                                        </Col>
                                                    </>
                                                );
                                            }
                                            return (
                                                <>
                                                    <Col xs={24} sm={12}>
                                                        <Form.Item name="receiver_region" label="Viloyat">
                                                            <Select
                                                                showSearch optionFilterProp="label" loading={catalogLoading}
                                                                placeholder="Viloyat" options={regionOptions}
                                                                onChange={(v) => { loadCities(v, 'receiver'); deliveryForm.setFieldsValue({ receiver_city_code: undefined, receiver_branch_code: undefined }); }}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={24} sm={12}>
                                                        <Form.Item name="receiver_city_code" label="Shahar kodi" rules={[{ required: true, message: 'Shaharni tanlang' }]}>
                                                            <Select
                                                                showSearch optionFilterProp="label" placeholder="Shahar" options={receiverCityOptions}
                                                                onChange={(v) => { if (isBranch) loadBranches(v); }}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    {isBranch && (
                                                        <Col xs={24}>
                                                            <Form.Item name="receiver_branch_code" label="Filial" rules={[{ required: true, message: 'Filialni tanlang' }]}>
                                                                <Select showSearch optionFilterProp="label" placeholder="Filial" options={branchOptions} />
                                                            </Form.Item>
                                                        </Col>
                                                    )}
                                                </>
                                            );
                                        }}
                                    </Form.Item>
                                </Row>
                            </Card>
                        </Col>
                    </Row>

                    <Divider />

                    {/* Yuk */}
                    <Card size="small" title={secTitle(<InboxOutlined />, 'Yuk', '#d97706')} style={{ borderRadius: 12 }} styles={{ header: { background: '#d977060f', borderBottom: '1px solid #d9770622' } }}>
                        <Row gutter={16}>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="weight" label="Vazn, kg" rules={[{ required: true, message: 'Vaznni kiriting' }]}>
                                    <InputNumber style={{ width: '100%' }} min={0.001} step={0.1} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="volume" label="Hajm">
                                    <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="piece" label="Joylar soni" rules={[{ required: true }]}>
                                    <InputNumber style={{ width: '100%' }} min={1} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="packageId" label="Qadoq turi">
                                    <Select allowClear showSearch optionFilterProp="label" loading={catalogLoading} placeholder="Qadoq turi" options={packageOptions} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item name="postTypeId" label="Yuborish turi">
                                    <Select allowClear showSearch optionFilterProp="label" loading={catalogLoading} placeholder="Yuborish turi" options={postTypeOptions} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Divider style={{ margin: '8px 0' }} orientation="left" plain>postTypes (buyurtma mahsulotlaridan avtomatik)</Divider>
                        <div>
                            {(Array.isArray(order?.items) ? order.items : []).map((it) => (
                                <Tag key={it.id} color="blue" style={{ marginBottom: 4 }}>
                                    {it.name} · {it.count} dona · {Number(it.price).toLocaleString()} ({it.pr_vr_id})
                                </Tag>
                            ))}
                        </div>
                    </Card>

                    <Divider />

                    {/* Qo'shimcha */}
                    <Card size="small" title={secTitle(<AppstoreOutlined />, "Qo'shimcha sozlamalar", '#7c3aed')} style={{ borderRadius: 12 }} styles={{ header: { background: '#7c3aed0f', borderBottom: '1px solid #7c3aed22' } }}>
                        <Row gutter={16}>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item name="bringBackMoney" label="Nalojniy to'lov">
                                    <Radio.Group optionType="button" buttonStyle="solid">
                                        <Radio.Button value={0}>Yo'q</Radio.Button>
                                        <Radio.Button value={1}>Ha</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item shouldUpdate={(p, c) => p.bringBackMoney !== c.bringBackMoney} noStyle>
                                    {({ getFieldValue }) => getFieldValue('bringBackMoney') === 1 && (
                                        <Form.Item name="back_money" label="Qaytariladigan summa" rules={[{ required: true, message: 'Summani kiriting' }]}>
                                            <InputNumber style={{ width: '100%' }} min={0} step={1000} />
                                        </Form.Item>
                                    )}
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item name="takePhoto" label="Foto talab">
                                    <Radio.Group optionType="button" buttonStyle="solid">
                                        <Radio.Button value={0}>Kerak emas</Radio.Button>
                                        <Radio.Button value={1}>Kerak</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item name="bringBackWaybill" label="Hujjat qaytarish">
                                    <Radio.Group optionType="button" buttonStyle="solid">
                                        <Radio.Button value={0}>Yo'q</Radio.Button>
                                        <Radio.Button value={1}>Ha</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item name="ready_to_take" label="Qabulga tayyor" valuePropName="checked">
                                    <Switch checkedChildren="Tayyor" unCheckedChildren="Kutishda" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <div style={{
                        position: 'sticky', bottom: 0, marginTop: 16,
                        display: 'flex', justifyContent: 'flex-end', gap: 12,
                        padding: '14px 4px 4px', borderTop: '1px solid var(--border, #eee)',
                        background: 'var(--bg-main, #f7f8fa)',
                    }}>
                        <Button size="large" onClick={closeDeliveryModal} style={{ minWidth: 130 }}>Bekor qilish</Button>
                        <Button
                            size="large" htmlType="submit" loading={deliverySubmitting} icon={<SendOutlined />}
                            style={{ minWidth: 200, background: '#6d28d9', borderColor: '#6d28d9', color: '#fff', fontWeight: 600 }}
                        >
                            BTS ga yuborish
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
}

export default OrderSingle;
